import * as React from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { BookPickerOverlayContext } from './book-picker-overlay';
import {
  BOOKING_HOUR_OPTIONS,
  BOOKING_MINUTE_OPTIONS,
  composeBookingTime,
  splitBookingTime,
  snapBookingDateTime
} from '@/lib/booking-time-step';

function datePartOf(value: string): string {
  return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : '';
}

function timePartOf(value: string): string {
  const match = /^\d{4}-\d{2}-\d{2}T(\d{2}:\d{2})(?::\d{2})?$/.exec(value);
  return match ? match[1] ?? '' : '';
}

export type TimeFieldProps = {
  timeFormat?: '12-hour' | '24-hour';
  timeAriaLabel?: string;
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  controlClassName?: string;
  hourAriaLabel?: string;
  minuteAriaLabel?: string;
};

/**
 * Time-of-day picker whose only options are 15-minute increments, so a time
 * such as 10:17 can never be chosen — not from the list and not by typing.
 */
export function TimeField({
  id,
  value,
  onValueChange,
  disabled,
  className,
  controlClassName,
  hourAriaLabel = 'Hour',
  minuteAriaLabel = 'Minute',
  timeFormat = '24-hour',
  timeAriaLabel = 'Time'
}: TimeFieldProps) {
  const overlayOwner = React.useContext(BookPickerOverlayContext);
  const [hourOpen, setHourOpen] = React.useState(false);
  const [minuteOpen, setMinuteOpen] = React.useState(false);
  const [timeOpen, setTimeOpen] = React.useState(false);
  const parts = splitBookingTime(value);
  const hour = parts?.hour ?? '';
  const minute = parts?.minute ?? '';
  const triggerClassName = cn('h-9 w-[4.75rem] px-2 text-xs tabular-nums', controlClassName);
  if (timeFormat === '12-hour') return (
    <Select open={overlayOwner ? timeOpen : undefined} onOpenChange={setTimeOpen}
      value={parts ? composeBookingTime(hour, minute) : ''} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger id={id} aria-label={timeAriaLabel} className={cn('h-9 w-32 px-2 text-sm tabular-nums', controlClassName, className)}>
        <SelectValue placeholder="Choose time" />
      </SelectTrigger>
      <SelectContent className="max-h-72" data-book-picker-owner={overlayOwner}
        onEscapeKeyDown={overlayOwner ? () => setTimeOpen(false) : undefined}>
        {BOOKING_HOUR_OPTIONS.flatMap(hour => BOOKING_MINUTE_OPTIONS.map(minute => {
          const time = composeBookingTime(hour, minute);
          return <SelectItem key={time} value={time}>{Number(hour) % 12 || 12}:{minute} {Number(hour) < 12 ? 'AM' : 'PM'}</SelectItem>;
        }))}
      </SelectContent>
    </Select>
  );
  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <Select
        open={overlayOwner ? hourOpen : undefined}
        onOpenChange={setHourOpen}
        value={hour}
        onValueChange={(next) => onValueChange(composeBookingTime(next, minute || '00'))}
        disabled={disabled}
      >
        <SelectTrigger id={id} aria-label={hourAriaLabel} className={triggerClassName}>
          <SelectValue placeholder="--" />
        </SelectTrigger>
        <SelectContent className="max-h-72" data-book-picker-owner={overlayOwner}
          onEscapeKeyDown={overlayOwner ? () => setHourOpen(false) : undefined}>
          {BOOKING_HOUR_OPTIONS.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span aria-hidden className="text-muted-foreground text-xs font-medium">
        :
      </span>
      <Select
        open={overlayOwner ? minuteOpen : undefined}
        onOpenChange={setMinuteOpen}
        value={minute}
        onValueChange={(next) => onValueChange(composeBookingTime(hour || '00', next))}
        disabled={disabled}
      >
        <SelectTrigger aria-label={minuteAriaLabel} className={triggerClassName}>
          <SelectValue placeholder="--" />
        </SelectTrigger>
        <SelectContent className="max-h-72" data-book-picker-owner={overlayOwner}
          onEscapeKeyDown={overlayOwner ? () => setMinuteOpen(false) : undefined}>
          {BOOKING_MINUTE_OPTIONS.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export type DateTimeFieldProps = {
  timeFormat?: TimeFieldProps['timeFormat'];
  timeAriaLabel?: string;
  /** Keep a date-only choice incomplete until the user chooses a time. */
  requireTimeSelection?: boolean;
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  min?: string;
  className?: string;
  controlClassName?: string;
  required?: boolean;
  'aria-label'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-describedby'?: string;
};

/**
 * Date plus 15-minute time entry. Emits the same `YYYY-MM-DDTHH:mm` shape as
 * a native datetime-local input, so existing wall-clock handling is unchanged.
 */
export function DateTimeField({
  requireTimeSelection = false,
  timeFormat,
  timeAriaLabel,
  id,
  value,
  onValueChange,
  disabled,
  min,
  className,
  controlClassName,
  required,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy
}: DateTimeFieldProps) {
  const [draftTime, setDraftTime] = React.useState('');
  const date = datePartOf(value);
  const time = timePartOf(value) || draftTime;
  const minDate = min ? datePartOf(min) : '';
  const emit = (nextDate: string, nextTime: string) => {
    if (!nextDate) {
      setDraftTime('');
      onValueChange('');
      return;
    }
    onValueChange(requireTimeSelection && !nextTime ? nextDate : snapBookingDateTime(`${nextDate}T${nextTime || '00:00'}`));
  };
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', timeFormat === '12-hour' && 'grid grid-cols-[minmax(0,1fr)_8rem] gap-2', className)}>
      <Input
        id={id}
        type="date"
        value={date}
        min={minDate || undefined}
        disabled={disabled}
        required={required}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        className={cn('h-9 w-[9.5rem] px-2 text-xs tabular-nums', controlClassName, timeFormat === '12-hour' && 'min-w-0 w-full text-sm')}
        onChange={(event) => emit(event.target.value, time)}
      />
      <TimeField
        timeFormat={timeFormat} timeAriaLabel={timeAriaLabel}
        value={time}
        onValueChange={(nextTime) => date ? emit(date, nextTime) : setDraftTime(nextTime)}
        disabled={disabled}
        controlClassName={controlClassName}
        hourAriaLabel={ariaLabel ? `${ariaLabel} hour` : 'Hour'}
        minuteAriaLabel={ariaLabel ? `${ariaLabel} minute` : 'Minute'}
      />
    </div>
  );
}
